import React from 'react';
import { useAuth0 } from '../hooks/useAuth0';
import { Box, CircularProgress, Typography } from '@mui/material';
import PatientDashboard from '../pages/patients/PatientDashboard';
import ProviderDashboard from '../pages/provider/ProviderDashboard';
import AdminDashboard from '../pages/admin/AdminDashboard';
import Dashboard from '../pages/Dashboard';

/**
 * ProtectedDashboard - Role-based dashboard router
 *
 * Routes users to the appropriate dashboard based on their actual role:
 * - patient → PatientDashboard
 * - provider → ProviderDashboard (KPIs, attention queue, CKD breakdown)
 * - admin → AdminDashboard (pending Provider/Researcher approvals, pending
 *   practice changes — Admin is the role with actual approval authority,
 *   so this is scoped to Admin only, not Researcher)
 * - researcher → Dashboard (the existing generic one)
 *
 * This previously read user?.['https://api.nephrotrax.com/roles'] — an
 * Auth0 custom-claim namespace Cognito never produces (this app migrated
 * off Auth0 some time ago; see useAuth0.jsx's buildUserFromClaims, which
 * already correctly reads the real "cognito:groups" claim into
 * user.roles). That meant roles was always empty here, and the "default
 * to patient when no roles are present" fallback below silently caught
 * every single user regardless of actual role — so a Provider logging in
 * always landed on PatientDashboard, never on their own dashboard, and
 * this router's Provider branch never existed at all. Fixed to read the
 * same user.roles the rest of the app (useRole.js, ProtectedRoute.jsx)
 * already correctly uses.
 */
const ProtectedDashboard = () => {
  const { user, isLoading } = useAuth0();

  // Show loading state while Auth0 is loading user data
  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          gap: 2,
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" color="text.secondary">
          Loading your dashboard...
        </Typography>
      </Box>
    );
  }

  const roles = user?.roles || [];
  const hasRole = (role) =>
    roles.some((r) => typeof r === 'string' && r.toLowerCase() === role.toLowerCase());

  const isProvider = hasRole('Provider');
  const isAdmin = hasRole('Admin');
  const isResearcher = hasRole('Researcher');

  // Default to Patient only when no recognized role is present at all —
  // covers a user whose role hasn't been assigned yet. With the fix
  // above, this now only fires for that genuine edge case, not for every
  // Provider/Admin/Researcher as it did before.
  const isPatient = hasRole('Patient') || (!isProvider && !isAdmin && !isResearcher);

  // Order matters here: Admin and Provider are checked BEFORE Patient.
  // Roles aren't necessarily mutually exclusive in Cognito — an account
  // (especially a test/dev one) can carry both an Admin and a Patient
  // group at once. Checking isPatient first meant that combination
  // always fell into PatientDashboard, which then found no real Patient
  // database row for that account and redirected to /profile — the exact
  // symptom this reordering fixes. Admin/Provider are the more specific,
  // higher-privilege roles, so they win regardless of what else is
  // attached to the account.
  if (isProvider) {
    return <ProviderDashboard />;
  }

  if (isAdmin) {
    return <AdminDashboard />;
  }

  if (isPatient) {
    return <PatientDashboard />;
  }

  // Researcher sees the existing generic Dashboard — Researcher isn't an
  // approval authority, so AdminDashboard's pending-approvals view isn't
  // relevant to them.
  return <Dashboard />;
};

export default ProtectedDashboard;